/**
 * Interview Simulation API
 * 面试模拟模块 API 服务
 */

import service from '@/utils/https';
import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types';
import type {
  InterviewSetupRequest,
  InterviewSetupResponse,
  InterviewStartResponse,
  InterviewReconnectResponse,
  InterviewEndRequest,
  InterviewResultsResponse,
  InterviewHistoryParams,
  InterviewHistoryResponse,
  InterviewStatisticsResponse,
  InterviewSetupPrefillResponse,
} from '@/types/interviewSimulation';

const inflightResultsRequests = new Map<string, Promise<AxiosResponse<ApiResponse<InterviewResultsResponse>>>>();

/**
 * 创建面试会话
 * POST /api/v1/interview/setup
 */
export function setupInterview(data: InterviewSetupRequest): Promise<AxiosResponse<ApiResponse<InterviewSetupResponse>>> {
  return service({
    url: '/v1/interview/setup',
    method: 'post',
    data,
  });
}

/**
 * 开始面试
 * POST /api/v1/interview/{session_id}/start
 */
export function startInterview(sessionId: string): Promise<AxiosResponse<ApiResponse<InterviewStartResponse>>> {
  return service({
    url: `/v1/interview/${sessionId}/start`,
    method: 'post',
  });
}

/**
 * 重连面试
 * POST /api/v1/interview/{session_id}/reconnect
 */
export function reconnectInterview(sessionId: string): Promise<AxiosResponse<ApiResponse<InterviewReconnectResponse>>> {
  return service({
    url: `/v1/interview/${sessionId}/reconnect`,
    method: 'post',
  });
}

/**
 * 结束面试
 * POST /api/v1/interview/{session_id}/end
 */
export function endInterview(sessionId: string, data: InterviewEndRequest): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/interview/${sessionId}/end`,
    method: 'post',
    data,
  });
}

/**
 * 获取面试结果（轮询）
 * GET /api/v1/interview/{session_id}/results
 */
export function getInterviewResults(sessionId: string): Promise<AxiosResponse<ApiResponse<InterviewResultsResponse>>> {
  const key = String(sessionId);
  const existing = inflightResultsRequests.get(key);
  if (existing) return existing;

  const request = service({
    url: `/v1/interview/${sessionId}/results`,
    method: 'get',
  });
  inflightResultsRequests.set(key, request);
  request.finally(() => {
    inflightResultsRequests.delete(key);
  });
  return request;
}

/**
 * 获取面试历史列表
 * GET /api/v1/interview/history
 */
export function getInterviewHistory(params?: InterviewHistoryParams): Promise<AxiosResponse<ApiResponse<InterviewHistoryResponse>>> {
  return service({
    url: '/v1/interview/history',
    method: 'get',
    params,
  });
}

/**
 * 归档面试记录
 * POST /api/v1/interview/{session_id}/archive
 */
export function archiveInterview(sessionId: string): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/interview/${sessionId}/archive`,
    method: 'post',
  });
}

/**
 * 取消归档面试记录
 * POST /api/v1/interview/{session_id}/unarchive
 */
export function unarchiveInterview(sessionId: string): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/interview/${sessionId}/unarchive`,
    method: 'post',
  });
}

/**
 * 获取面试统计数据
 * GET /api/v1/interview/statistics
 */
export function getInterviewStatistics(): Promise<AxiosResponse<ApiResponse<InterviewStatisticsResponse>>> {
  return service({
    url: '/v1/interview/statistics',
    method: 'get',
  });
}

/**
 * 获取面试设置预填写数据
 * GET /api/v1/prefill/interview-setup
 */
export function getInterviewSetupPrefill(): Promise<AxiosResponse<ApiResponse<InterviewSetupPrefillResponse>>> {
  return service({
    url: '/v1/prefill/interview-setup',
    method: 'get',
  });
}
